/**
 * Discovery of client game modules. Nothing to register by hand: every
 * folder `games/<id>/` with an `index.ts` is a module, and every
 * `games/<id>/tile.svg` is its start-page tile.
 *
 * - Module code is loaded lazily (`import.meta.glob` without `eager`), so Vite
 *   splits each module into its own chunk; the student page never downloads
 *   the Jeopardy editor.
 * - Tiles are loaded eagerly as raw SVG strings; they are small and the start
 *   page needs all of them at once. A tile may exist before the module's code
 *   does (the design is drawn first).
 *
 * The folder name must equal the server manifest id.
 */
import type { GameClient } from '@shared/game-client';

const loaders = import.meta.glob<{ default: GameClient }>('./*/index.ts');
const tileFiles = import.meta.glob<string>('./*/tile.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
});

function idFromPath(path: string): string {
  return path.split('/')[1] ?? '';
}

const loaderById = new Map(Object.entries(loaders).map(([path, load]) => [idFromPath(path), load]));
const tileById = new Map(Object.entries(tileFiles).map(([path, svg]) => [idFromPath(path), svg]));

/** Ids of every module that has client code. */
export function clientModuleIds(): string[] {
  return [...loaderById.keys()].sort();
}

/** Load a module's client code, or null if this build has none for the id. */
export async function loadGameClient(id: string): Promise<GameClient | null> {
  const load = loaderById.get(id);
  if (load === undefined) return null;
  return (await load()).default;
}

/** The tile SVG for an id, or null. Synchronous: tiles are bundled eagerly. */
export function gameTile(id: string): string | null {
  return tileById.get(id) ?? null;
}
